/**
 * Phase 8 T-06c §21.D — Smart Match Preferences modal.
 *
 * Three inner tabs:
 *   1. `General`        — score threshold, geographic scope, seniority range
 *   2. `Visibility`     — who in the org can see this search
 *   3. `Shared context` — company / team description + must-haves (mirrors
 *                          the §14 onboarding Shared Context surface)
 *
 * Footer: a single emerald `[Save preferences]` button (no per-tab save).
 * Last-opened tab is persisted under `ggj.smartmatch.preferences.lastTab`
 * per §21.H acceptance.
 */

import { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import {
  DEFAULT_PREFERENCES,
  readLastPreferencesTab,
  readPreferences,
  readPreferencesV1,
  writeLastPreferencesTab,
  writePreferences,
  writePreferencesV1,
  type PreferencesTab,
  type SmartMatchPreferences,
  type VisibilityScope,
} from '../lib/smartMatchPreferences';

interface PreferencesModalProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const TABS: Array<{ id: PreferencesTab; label: string }> = [
  { id: 'general', label: 'General' },
  { id: 'visibility', label: 'Visibility' },
  { id: 'shared-context', label: 'Shared context' },
];

export function PreferencesModal({ open, onClose, onSaved }: PreferencesModalProps) {
  const [tab, setTab] = useState<PreferencesTab>('general');
  const [prefs, setPrefs] = useState<SmartMatchPreferences>(DEFAULT_PREFERENCES);

  const updatePrefs = useCallback((next: SmartMatchPreferences) => {
    setPrefs(next);
    writePreferencesV1(next);
  }, []);

  useEffect(() => {
    if (!open) return;
    setTab(readLastPreferencesTab());
    setPrefs(readPreferencesV1() ?? readPreferences());
  }, [open]);

  // Remember the last opened tab — §21.H acceptance.
  useEffect(() => {
    if (!open) return;
    writeLastPreferencesTab(tab);
  }, [open, tab]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  if (!open) return null;

  const save = () => {
    writePreferencesV1(prefs);
    writePreferences(prefs);
    onSaved?.();
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ggj-smartmatch-prefs-title"
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-[640px] flex-col overflow-hidden rounded-[12px] border shadow-2xl"
        style={{
          background: 'var(--vscode-bg-elevated)',
          borderColor: 'var(--vscode-border-primary)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between border-b px-5 py-3"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          <div>
            <h2
              id="ggj-smartmatch-prefs-title"
              className="text-[15px] font-semibold"
              style={{ color: 'var(--vscode-text-primary)' }}
            >
              Search preferences
            </h2>
            <p
              className="text-[11px]"
              style={{ color: 'var(--vscode-text-tertiary)' }}
            >
              Applies to all Smart Match searches in this workspace.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[color:var(--vscode-text-tertiary)] hover:text-[color:var(--vscode-text-primary)]"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div
          role="tablist"
          aria-label="Preferences sections"
          className="flex gap-1 border-b px-4"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.id)}
                className="relative inline-flex h-10 items-center px-3 text-[13px] font-medium transition-colors"
                style={{
                  color: active
                    ? 'var(--vscode-text-primary)'
                    : 'var(--vscode-text-secondary)',
                }}
              >
                {t.label}
                {active ? (
                  <span
                    aria-hidden
                    className="absolute inset-x-3 bottom-0 h-0.5 rounded-t"
                    style={{ background: 'var(--ggj-accent,#10B981)' }}
                  />
                ) : null}
              </button>
            );
          })}
        </div>

        <div className="min-h-[280px] px-5 py-4">
          {tab === 'general' ? (
            <GeneralTab prefs={prefs} onChange={updatePrefs} />
          ) : tab === 'visibility' ? (
            <VisibilityTab prefs={prefs} onChange={updatePrefs} />
          ) : (
            <SharedContextTab prefs={prefs} onChange={updatePrefs} />
          )}
        </div>

        <div
          className="flex items-center justify-end gap-2 border-t px-5 py-3"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 items-center rounded-[10px] border px-3 text-[13px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
            style={{
              borderColor: 'var(--vscode-border-primary)',
              color: 'var(--vscode-text-primary)',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            className="inline-flex h-9 items-center rounded-[10px] bg-[var(--ggj-accent,#10B981)] px-4 text-[13px] font-medium text-white transition-opacity hover:opacity-90"
          >
            Save preferences
          </button>
        </div>
      </div>
    </div>
  );
}

interface TabProps {
  prefs: SmartMatchPreferences;
  onChange: (next: SmartMatchPreferences) => void;
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label
      className="block text-[11px] font-semibold uppercase tracking-wider"
      style={{ color: 'var(--vscode-text-tertiary)' }}
    >
      {children}
    </label>
  );
}

function GeneralTab({ prefs, onChange }: TabProps) {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <FieldLabel>Minimum match score</FieldLabel>
        <div className="mt-1 flex items-center gap-3">
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={prefs.general.scoreThreshold}
            onChange={(e) =>
              onChange({
                ...prefs,
                general: {
                  ...prefs.general,
                  scoreThreshold: Number(e.target.value),
                },
              })
            }
            className="flex-1 accent-[var(--ggj-accent,#10B981)]"
            aria-label="Minimum match score"
          />
          <span
            className="w-12 text-right tabular-nums text-[13px] font-medium"
            style={{ color: 'var(--vscode-text-primary)' }}
          >
            {prefs.general.scoreThreshold}%
          </span>
        </div>
      </div>

      <div>
        <FieldLabel>Geographic scope</FieldLabel>
        <input
          type="text"
          value={prefs.general.geography}
          onChange={(e) =>
            onChange({
              ...prefs,
              general: { ...prefs.general, geography: e.target.value },
            })
          }
          placeholder="Global · Europe · San Francisco Bay"
          className="mt-1 h-9 w-full rounded-[8px] border px-3 text-[13px] outline-none"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            background: 'var(--vscode-bg-primary)',
            color: 'var(--vscode-text-primary)',
          }}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel>Seniority min</FieldLabel>
          <input
            type="text"
            value={prefs.general.seniorityMin}
            onChange={(e) =>
              onChange({
                ...prefs,
                general: { ...prefs.general, seniorityMin: e.target.value },
              })
            }
            className="mt-1 h-9 w-full rounded-[8px] border px-3 text-[13px] outline-none"
            style={{
              borderColor: 'var(--vscode-border-primary)',
              background: 'var(--vscode-bg-primary)',
              color: 'var(--vscode-text-primary)',
            }}
          />
        </div>
        <div>
          <FieldLabel>Seniority max</FieldLabel>
          <input
            type="text"
            value={prefs.general.seniorityMax}
            onChange={(e) =>
              onChange({
                ...prefs,
                general: { ...prefs.general, seniorityMax: e.target.value },
              })
            }
            className="mt-1 h-9 w-full rounded-[8px] border px-3 text-[13px] outline-none"
            style={{
              borderColor: 'var(--vscode-border-primary)',
              background: 'var(--vscode-bg-primary)',
              color: 'var(--vscode-text-primary)',
            }}
          />
        </div>
      </div>
    </div>
  );
}

function VisibilityTab({ prefs, onChange }: TabProps) {
  const options: Array<{ id: VisibilityScope; label: string; desc: string }> = [
    { id: 'private', label: 'Private', desc: 'Only you can see this search.' },
    { id: 'team', label: 'Team', desc: 'Members on your recruiter team.' },
    { id: 'org', label: 'Organisation', desc: 'Everyone in your workspace.' },
  ];

  return (
    <div className="flex flex-col gap-2">
      {options.map((opt) => {
        const active = prefs.visibility.scope === opt.id;
        return (
          <label
            key={opt.id}
            className={`flex cursor-pointer items-start gap-3 rounded-[10px] border p-3 transition-colors ${
              active ? '' : 'hover:bg-[var(--vscode-bg-hover)]'
            }`}
            style={{
              borderColor: active
                ? 'var(--ggj-accent,#10B981)'
                : 'var(--vscode-border-primary)',
              background: active
                ? 'var(--ggj-accent-soft,#D1FAE5)'
                : 'transparent',
            }}
          >
            <input
              type="radio"
              name="visibility-scope"
              checked={active}
              onChange={() =>
                onChange({
                  ...prefs,
                  visibility: { scope: opt.id },
                })
              }
              className="mt-1 accent-[var(--ggj-accent,#10B981)]"
            />
            <div>
              <p
                className="text-[13px] font-medium"
                style={{
                  color: active ? '#065F46' : 'var(--vscode-text-primary)',
                }}
              >
                {opt.label}
              </p>
              <p
                className="mt-0.5 text-[11px]"
                style={{ color: 'var(--vscode-text-tertiary)' }}
              >
                {opt.desc}
              </p>
            </div>
          </label>
        );
      })}
    </div>
  );
}

function SharedContextTab({ prefs, onChange }: TabProps) {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <FieldLabel>Company description</FieldLabel>
        <textarea
          rows={3}
          value={prefs.sharedContext.companyDescription}
          onChange={(e) =>
            onChange({
              ...prefs,
              sharedContext: {
                ...prefs.sharedContext,
                companyDescription: e.target.value,
              },
            })
          }
          placeholder="Who we are, what we build, how we work…"
          className="mt-1 w-full resize-none rounded-[8px] border px-3 py-2 text-[13px] outline-none"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            background: 'var(--vscode-bg-primary)',
            color: 'var(--vscode-text-primary)',
          }}
        />
      </div>
      <div>
        <FieldLabel>Team description</FieldLabel>
        <textarea
          rows={3}
          value={prefs.sharedContext.teamDescription}
          onChange={(e) =>
            onChange({
              ...prefs,
              sharedContext: {
                ...prefs.sharedContext,
                teamDescription: e.target.value,
              },
            })
          }
          placeholder="What this team owns, who they work with…"
          className="mt-1 w-full resize-none rounded-[8px] border px-3 py-2 text-[13px] outline-none"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            background: 'var(--vscode-bg-primary)',
            color: 'var(--vscode-text-primary)',
          }}
        />
      </div>
      <div>
        <FieldLabel>Must-haves</FieldLabel>
        <textarea
          rows={2}
          value={prefs.sharedContext.mustHaves}
          onChange={(e) =>
            onChange({
              ...prefs,
              sharedContext: {
                ...prefs.sharedContext,
                mustHaves: e.target.value,
              },
            })
          }
          placeholder="Comma-separated hard requirements that apply to every search."
          className="mt-1 w-full resize-none rounded-[8px] border px-3 py-2 text-[13px] outline-none"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            background: 'var(--vscode-bg-primary)',
            color: 'var(--vscode-text-primary)',
          }}
        />
      </div>
    </div>
  );
}
